/**
 * Générateur — Exploiter les relations k(a + b) = ka + kb ou k(a − b) = ka − kb
 * pour factoriser, ou développer une expression littérale.
 * Attendu ID : 936 | Niveau : 5ème | Domaine : Calcul littéral (cluster 5, O38)
 *
 * Source : REPLI SÉSAMATH (décision utilisateur 2026-07-11 — pas d'exercice
 * dans le doc prof). Cahier Sésamath 5e chap. 05, Série 4 « Distributivité
 * littérale » (p69-71), ex. 6, 7, 9, 10, 12, 14, 16, 18, 19.
 *
 * Anti-doublons :
 *   - le « calcule astucieusement » NUMÉRIQUE (ex. 3, 11) est porté par
 *     gen_906 (O8, distributivité simple numérique) — non repris ici ;
 *   - le mixte « développer PUIS réduire » (ex. 6 D/F) est porté par gen_937
 *     (O39 — c'est la réduction qui y est notée).
 *
 * Plan de cartes (validé 2026-07-11) :
 *
 *   Carte « developper » — ex. 6/7 : texte_trou_saisie k(x ± b) = {{k}}x ± {{kb}}
 *     (formes k(x+b), k(x−b), k(b−x), miroir (x+b)×k) + qcm_unique (dont les
 *     formes à lettres a(b + c) du doc — distracteurs : distribue sur un seul
 *     terme, additionne au lieu de multiplier).
 *
 *   Carte « erreur » — ex. 9 (Margaux/Steph/Abdou) : qcm_unique de la
 *     correction. Familles de misconceptions RÉELLES du doc : k(x+m) = kx+m
 *     (distribue sur un seul terme), k(b−a) = kb−a, k(x+m)+p = kx+m+p
 *     (priorité). Distracteurs vérifiés distincts par construction.
 *
 *   Carte « factoriser » — ex. 12/14/19 : texte_trou_saisie
 *     Ax + B = {{g}} × ({{u}}x + {{v}}) avec g = pgcd construit ≥ 2 et
 *     pgcd(u, v) = 1 par construction (tirage de u, v premiers entre eux puis
 *     A = gu, B = gv) ; cas b = 1 du doc (7a + 7) en rotation + qcm_unique
 *     (« factorisation la plus aboutie » ; quand g est composé, un distracteur
 *     est la factorisation PARTIELLE — égalité vraie mais pas aboutie, LE
 *     point de l'ex. 18/19) + tableau_vf (ex. 18 ADAPTÉ : les items du doc
 *     sont tous vrais en tant qu'égalités → items faux introduits par
 *     construction, constante mal divisée).
 *
 *   Carte « aire » — ex. 10/16 (l'appui « aires » du cadrage) : SVG rectangle
 *     largeur x + b découpé en deux, hauteur k. texte_trou_saisie (forme
 *     factorisée + forme développée) + qcm_unique constat (« égales pour
 *     toute valeur de x »). Le volet « dessine deux schémas » du doc est
 *     SUPPRIMÉ (non corrigeable) → remplacé par le QCM de constat.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 936;

const L_DEVELOPPER = 'Développer k(a + b)';
const L_ERREUR = 'Corriger un développement faux';
const L_FACTORISER = 'Factoriser une expression';
const L_AIRE = 'L\'aire du rectangle de deux façons';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement, jamais en saisie)

const LETTRES = ['x', 'n', 't', 'a'];

// pgcd euclidien (entiers > 0)
const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

// ═════════ Carte 1 — Développer (ex. 6/7) ═════════

const genDevelopperTrous = () => {
    const forme = randomChoice(['plus', 'moins', 'inverse', 'miroir']);
    const L = randomChoice(LETTRES);
    const k = randInt(2, 9);
    const b = randInt(2, 9);
    let texte, phrase, params;
    if (forme === 'plus') {
        texte = `<p><b>Développe l'expression ${k}(${L} + ${b}).</b></p>`;
        phrase = `${k}(${L} + ${b}) = {{${k}}}${L} + {{${k * b}}}`;
        params = { forme, L, k, b };
    } else if (forme === 'moins') {
        texte = `<p><b>Développe l'expression ${k}(${L} ${MOINS} ${b}).</b></p>`;
        phrase = `${k}(${L} ${MOINS} ${b}) = {{${k}}}${L} ${MOINS} {{${k * b}}}`;
        params = { forme, L, k, b };
    } else if (forme === 'inverse') {
        // k(b − x) = kb − kx : l'ordre des termes est inversé
        texte = `<p><b>Développe l'expression ${k}(${b} ${MOINS} ${L}).</b></p>`;
        phrase = `${k}(${b} ${MOINS} ${L}) = {{${k * b}}} ${MOINS} {{${k}}}${L}`;
        params = { forme, L, k, b };
    } else {
        // (x + b) × k = kx + kb : le facteur est à droite
        texte = `<p><b>Développe l'expression (${L} + ${b}) × ${k}.</b></p>`;
        phrase = `(${L} + ${b}) × ${k} = {{${k}}}${L} + {{${k * b}}}`;
        params = { forme, L, k, b };
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_DEVELOPPER), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};

const genDevelopperQcm = () => {
    const forme = randomChoice(['nombre', 'lettres']);
    const k = randInt(2, 9);
    const b = randInt(2, 9);
    let texte, correct, candidats, params;
    if (forme === 'nombre') {
        const L = randomChoice(LETTRES);
        const signe = randomChoice(['+', MOINS]);
        texte = `<p><b>Développe : ${k}(${L} ${signe} ${b}).</b></p>`;
        correct = `${k}${L} ${signe} ${k * b}`;
        candidats = [
            `${k}${L} ${signe} ${b}`,             // distribue sur un seul terme (LE piège)
            `${k + 1}${L} ${signe} ${k * b}`,     // coefficient faux
            `${k * b}${L} ${signe} ${k}`,         // les deux produits échangés
        ];
        params = { forme, L, k, b, signe };
    } else {
        // a(b + c) : les lettres du doc (ex. 7c)
        texte = `<p><b>Développe : a(b + ${b}).</b></p>`;
        correct = `ab + ${b}a`;
        candidats = [
            `ab + ${b}`,           // distribue sur un seul terme
            `${b}ab`,              // tout multiplié
            `a + ${b}a`,           // premier produit oublié
        ];
        params = { forme, b };
    }
    const distincts = [...new Set(candidats)].filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_DEVELOPPER));
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 2 — Corriger un développement faux (ex. 9) ═════════

const PRENOMS = ['Margaux', 'Steph', 'Abdou', 'Lina', 'Noah', 'Sofia'];

const genErreurQcm = () => {
    const cas = randomChoice(['unSeulTerme', 'inverse', 'priorite']);
    const prenom = randomChoice(PRENOMS);
    const L = randomChoice(LETTRES);
    const k = randInt(2, 9);
    const m = randInt(2, 9);
    let texte, correct, candidats, params;
    if (cas === 'unSeulTerme') {
        // Margaux : 2(x + 3) = 2x + 3
        texte = `<p><b>${prenom} a écrit : ${k}(${L} + ${m}) = ${k}${L} + ${m}.</b> `
            + 'C\'est faux ! Quelle est la bonne écriture développée ?</p>';
        correct = `${k}${L} + ${k * m}`;
        candidats = [
            `${k}${L} + ${m}`,          // l'erreur d'origine
            `${k}${L} + ${k + m}`,      // additionne au lieu de multiplier
            `${k * m}${L} + ${k}`,      // produits échangés
        ];
        params = { cas, L, k, m };
    } else if (cas === 'inverse') {
        // Steph : 5(4 − a) = 20 − a
        texte = `<p><b>${prenom} a écrit : ${k}(${m} ${MOINS} ${L}) = ${k * m} ${MOINS} ${L}.</b> `
            + 'C\'est faux ! Quelle est la bonne écriture développée ?</p>';
        correct = `${k * m} ${MOINS} ${k}${L}`;
        candidats = [
            `${k * m} ${MOINS} ${L}`,       // l'erreur d'origine
            `${k * m} + ${k}${L}`,          // signe perdu
            `${k}${L} ${MOINS} ${k * m}`,   // termes échangés
        ];
        params = { cas, L, k, m };
    } else {
        // Abdou : 3(x + 2) + 4 = 3x + 2 + 4
        const p = randInt(2, 9);
        texte = `<p><b>${prenom} a écrit : ${k}(${L} + ${m}) + ${p} = ${k}${L} + ${m} + ${p}.</b> `
            + 'C\'est faux ! Quelle est la bonne écriture développée ?</p>';
        correct = `${k}${L} + ${k * m} + ${p}`;
        candidats = [
            `${k}${L} + ${m} + ${p}`,             // l'erreur d'origine
            `${k}${L} + ${k * m} + ${k * p}`,     // le k distribué AUSSI sur p
            `${k}${L} + ${k * (m + p)}`,          // parenthèse étendue à p
        ];
        params = { cas, L, k, m, p };
    }
    const distincts = [...new Set(candidats)].filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_ERREUR));
    payload._meta.params = params;
    return payload;
};

// ═════════ Carte 3 — Factoriser (ex. 12/14/19) ═════════

// couples (u, v) premiers entre eux, u ≠ v (énumérés : contrainte structurelle).
// u ≥ 2 pour éviter l'écriture « 1x » — le cas facteur 1 est porté par la
// variante « bUn » (7a + 7 = 7(a + 1)).
const COUPLES_UV = [];
for (let u = 2; u <= 9; u++) {
    for (let v = 1; v <= 9; v++) {
        if (u !== v && pgcd(u, v) === 1) COUPLES_UV.push([u, v]);
    }
}

/** Ax + B = g(ux + v) avec g ≥ 2 et pgcd(u, v) = 1 PAR CONSTRUCTION. */
const tirerFactorisation = () => {
    const g = randInt(2, 7);
    const [u, v] = randomChoice(COUPLES_UV);
    return { g, u, v, A: g * u, B: g * v };
};

const genFactoriserTrous = () => {
    const forme = randomChoice(['classique', 'bUn']);
    const L = randomChoice(LETTRES);
    let texte, phrase, params;
    // Les trous SANS ORDRE de cette carte : voir plus bas, seule la forme « bUn » en a.
    let sansOrdre = [];
    if (forme === 'classique') {
        const { g, u, v, A, B } = tirerFactorisation();
        const signe = randomChoice(['+', MOINS]);
        texte = `<p><b>Factorise ${A}${L} ${signe} ${B} en mettant en évidence le plus grand `
            + 'facteur commun.</b></p>';
        phrase = `${A}${L} ${signe} ${B} = {{${g}}} × ({{${u}}}${L} ${signe} {{${v}}})`;
        params = { forme, L, g, u, v, signe };
    } else {
        // le cas b = 1 du doc : ka + k = k(a + 1)
        const k = randInt(2, 9);
        texte = `<p><b>Factorise ${k}${L} + ${k}.</b> `
            + `Souviens-toi : ${k} = ${k} × 1.</p>`;
        phrase = `${k}${L} + ${k} = {{${k}}} × ({{${L}}} + {{1}})`;
        // La parenthèse porte une SOMME de deux termes indépendants : l'élève qui écrit
        // 9 × (1 + x) a autant raison que celui qui écrit 9 × (x + 1), et la correction,
        // positionnelle par défaut, comptait le premier faux (retour prof 2026-09-24).
        // Les trous 1 et 2 sont ces deux termes ; le facteur commun (trou 0) reste à sa place.
        //
        // La forme « classique » n'a PAS ce réglage : ses deux trous de parenthèse sont le
        // coefficient collé à la lettre et la constante, deux rôles différents — échanger
        // « 3x + 5 » en « 5x + 3 » change la valeur.
        sansOrdre = [[1, 2]];
        params = { forme, L, k };
    }
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_FACTORISER), mode: 'saisie', trousInterchangeables: sansOrdre,
    });
    payload._meta.params = params;
    return payload;
};

const genFactoriserQcm = () => {
    const L = randomChoice(LETTRES);
    const { g, u, v, A, B } = tirerFactorisation();
    const texte = `<p><b>Quelle est la factorisation LA PLUS ABOUTIE de ${A}${L} + ${B} ?</b></p>`;
    const correct = `${g}(${u}${L} + ${v})`;
    const candidats = [
        `${g}(${u}${L} + ${B})`,        // n'a divisé que le premier terme
        `${u}(${g}${L} + ${v})`,        // facteur et coefficient échangés
        `${g}(${A}${L} + ${B})`,        // facteur sorti mais AUCUN terme divisé
        // (garantie ≥ 3 choix : ce candidat et le premier sont toujours
        // distincts du correct et entre eux — A = gu ≠ u et B = gv ≠ v)
    ];
    // quand g est composé : la factorisation PARTIELLE (égalité VRAIE mais pas
    // aboutie — le point de l'ex. 18/19) ; sinon : constante mal divisée
    const diviseur = [2, 3, 5].find(d => g % d === 0 && d < g);
    if (diviseur) {
        candidats.push(`${diviseur}(${(g / diviseur) * u}${L} + ${(g / diviseur) * v})`);
    } else {
        candidats.push(`${g}(${u}${L} + ${v + 1})`);
    }
    const distincts = [...new Set(candidats)].filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distincts], correct, texte, opts(L_FACTORISER));
    payload._meta.params = { L, g, u, v };
    return payload;
};

const genFactoriserVf = () => {
    const L = randomChoice(LETTRES);
    const lignes = [];
    const bonnes = [];
    // 4 lignes construites sur DEUX factorisations distinctes pour varier
    const t1 = tirerFactorisation();
    let t2 = tirerFactorisation();
    for (let essai = 0; essai < 30 && t2.A === t1.A && t2.B === t1.B; essai++) t2 = tirerFactorisation();
    if (t2.A === t1.A && t2.B === t1.B) t2 = { g: t1.g + 1, u: t1.u, v: t1.v, A: (t1.g + 1) * t1.u, B: (t1.g + 1) * t1.v };
    // vraie aboutie ; vraie PARTIELLE si g pair (l'ex. 18 du doc : 2(4x+6)) ;
    // fausse : constante mal divisée
    lignes.push(`${t1.A}${L} + ${t1.B} = ${t1.g}(${t1.u}${L} + ${t1.v})`);
    bonnes.push(0);
    if (t1.g % 2 === 0 && t1.g > 2) {
        lignes.push(`${t1.A}${L} + ${t1.B} = 2(${(t1.g / 2) * t1.u}${L} + ${(t1.g / 2) * t1.v})`);
        bonnes.push(0);   // VRAIE (égalité correcte, même si pas aboutie)
    } else {
        lignes.push(`${t1.A}${L} + ${t1.B} = ${t1.g}(${t1.u}${L} + ${t1.v + 1})`);
        bonnes.push(1);   // fausse : constante mal divisée
    }
    lignes.push(`${t2.A}${L} + ${t2.B} = ${t2.g}(${t2.u}${L} + ${t2.B})`);
    bonnes.push(1);       // fausse : constante non divisée
    lignes.push(`${t2.A}${L} + ${t2.B} = ${t2.g}(${t2.u}${L} + ${t2.v})`);
    bonnes.push(0);
    // ordre imprévisible : mélange conjoint lignes/vérités
    const paires = shuffle(lignes.map((l, i) => [l, bonnes[i]]));
    lignes.length = 0;
    bonnes.length = 0;
    paires.forEach(([l, bo]) => { lignes.push(l); bonnes.push(bo); });
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque ligne, développe le membre de droite '
        + 'et compare. Attention : une égalité peut être vraie même si la '
        + 'factorisation n\'est pas la plus aboutie !</p>',
        opts(L_FACTORISER));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

// ═════════ Carte 4 — L'aire du rectangle (ex. 10/16) ═════════

// rectangle hauteur k, largeur x + b, découpé en deux par un trait vertical
const svgRectangle = (k, b) => {
    const u = 20;
    const xVis = 4;             // largeur VISUELLE de la partie « x »
    const x0 = 30, y0 = 26;
    const W = (xVis + b) * u, H = Math.min(k, 6) * u;
    const label = (x, y, txt) =>
        `<text x='${x}' y='${y}' text-anchor='middle' font-size='13' fill='#1E3A8A' font-family='sans-serif'>${txt}</text>`;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${x0 + W + 16} ${y0 + H + 14}'>`
        + `<rect x='${x0}' y='${y0}' width='${xVis * u}' height='${H}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`
        + `<rect x='${x0 + xVis * u}' y='${y0}' width='${b * u}' height='${H}' fill='#C7D2FE' stroke='#1E3A8A' stroke-width='2'/>`
        + label(x0 + (xVis * u) / 2, y0 - 8, 'x')
        + label(x0 + xVis * u + (b * u) / 2, y0 - 8, String(b))
        + `<text x='${x0 - 12}' y='${y0 + H / 2 + 5}' text-anchor='middle' font-size='13' fill='#1E3A8A' font-family='sans-serif'>${k}</text>`
        + `</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const TEXTE_RECT = (k, b) =>
    `<p><b>Un rectangle a pour hauteur ${k} et pour largeur x + ${b} (figure ci-dessous).</b></p>`;

const genAireTrous = () => {
    const k = randInt(2, 6);
    const b = randInt(2, 9);
    const phrase = `Forme factorisée : aire = ${k} × (x + {{${b}}}). `
        + `Forme développée : aire = {{${k}}}x + {{${k * b}}}.`;
    const payload = makeTexteTrou(phrase,
        TEXTE_RECT(k, b) + '<p>Exprime son aire de deux façons.</p>',
        { ...opts(L_AIRE), mode: 'saisie', elements_additionnels: svgRectangle(k, b) });
    payload._meta.params = { k, b };
    return payload;
};

const genAireQcm = () => {
    const k = randInt(2, 6);
    const b = randInt(2, 9);
    const texte = TEXTE_RECT(k, b)
        + `<p>Son aire s'écrit ${k}(x + ${b}) ou ${k}x + ${k * b}. Que peut-on dire de ces deux expressions ?</p>`;
    const correct = 'Elles sont égales pour toute valeur de x.';
    const candidats = [
        `Elles ne sont égales que pour x = ${b}.`,
        'Elles ne sont jamais égales.',
        `Elles ne sont égales que si x est plus grand que ${b}.`,
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_AIRE), elements_additionnels: svgRectangle(k, b) });
    payload._meta.params = { k, b };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    developper_texte_trou_saisie: genDevelopperTrous,
    developper_qcm_unique: genDevelopperQcm,
    erreur_qcm_unique: genErreurQcm,
    factoriser_texte_trou_saisie: genFactoriserTrous,
    factoriser_qcm_unique: genFactoriserQcm,
    factoriser_tableau_vf: genFactoriserVf,
    aire_texte_trou_saisie: genAireTrous,
    aire_qcm_unique: genAireQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'developper', label: L_DEVELOPPER, formatsList: buildFormats('developper') },
    { id: 'erreur', label: L_ERREUR, formatsList: buildFormats('erreur') },
    { id: 'factoriser', label: L_FACTORISER, formatsList: buildFormats('factoriser') },
    { id: 'aire', label: L_AIRE, formatsList: buildFormats('aire') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
